/**
 * Progress event handler — processes SSE chat events into message state.
 */

import { runInAction } from 'mobx';
import type { ChatEvent } from '@skjv/common';
import type { ChatMessage } from './types';
import { theaterEventPatch } from './theater-events';

export function handleProgressEvent(
  messages: ChatMessage[],
  setLoading: (v: boolean) => void,
  persistLocal: () => void,
  loadingId: string,
  event: ChatEvent,
): void {
  runInAction(() => {
    const idx = messages.findIndex((m) => m.id === loadingId);
    if (idx === -1) return;
    const msg = messages[idx];

    const theaterPatch = theaterEventPatch(msg, event);
    if (theaterPatch) {
      messages[idx] = { ...msg, ...theaterPatch };
    } else if (event.type === 'queue') {
      // 'full' never reaches here — the transport throws QueueFullError.
      messages[idx] = {
        ...msg,
        queueStatus: event.state === 'waiting'
          ? {
              state: 'waiting',
              position: event.position,
              depth: event.depth,
              estimatedWaitMs: event.estimatedWaitMs,
              receivedAt: Date.now(),
            }
          : undefined,
      };
    } else if (event.type === 'step') {
      const steps = [...(msg.progressSteps ?? [])];
      if (event.status === 'running') {
        steps.push({ step: event.step, status: 'running' });
      } else {
        const s = steps.find((s) => s.step === event.step);
        if (s) { s.status = 'done'; s.durationMs = event.durationMs; }
      }
      messages[idx] = { ...msg, progressSteps: steps };
    } else if (event.type === 'inference-progress') {
      messages[idx] = {
        ...msg,
        inferenceProgress: { tokenCount: event.tokenCount, elapsedMs: event.elapsedMs },
      };
    } else if (event.type === 'inference-meta') {
      messages[idx] = {
        ...msg,
        inferenceMeta: {
          hostname: event.hardware.hostname,
          cpuModel: event.hardware.cpuModel,
          cpuCores: event.hardware.cpuCores,
          totalMemoryGb: event.hardware.totalMemoryGb,
          freeMemoryGb: event.hardware.freeMemoryGb,
          platform: event.hardware.platform,
          arch: event.hardware.arch,
          ollamaHost: event.hardware.ollamaHost,
          model: event.model,
          maxTokens: event.maxTokens,
        },
      };
    } else if (event.type === 'content-chunk') {
      const chunks = [...(msg.contentChunks ?? [])];
      chunks.push({
        section: event.section,
        content: event.content,
        reference: event.reference,
      });
      messages[idx] = { ...msg, contentChunks: chunks };
    } else if (event.type === 'scripture-response') {
      // Scripture-first: DB-driven response, message is complete immediately
      messages[idx] = {
        ...msg,
        isLoading: false,
        scriptureResponse: event.data,
        commentaryState: 'not-requested',
        intent: event.intent.intent,
        content: event.data.verses.map((v) => `${v.reference}: ${v.text}`).join('\n'),
      };
      setLoading(false);
      persistLocal();
    } else if (event.type === 'search-results') {
      // Book-scoped search: deterministic SQLite results, no LLM
      messages[idx] = {
        ...msg,
        isLoading: false,
        intent: 'book_search',
        searchResults: {
          query: event.query,
          bookName: event.bookName,
          results: event.results,
          total: event.total,
        },
        content: `Found ${event.total} results for "${event.query}" in ${event.bookName}`,
      };
      setLoading(false);
      persistLocal();
    } else if (event.type === 'locator-results') {
      // Shape 2 locator: verified verse citations, no commentary
      messages[idx] = {
        ...msg,
        isLoading: false,
        intent: 'locator',
        locatorResults: {
          query: event.query,
          entityName: event.entityName,
          results: event.results,
        },
        content: event.results.map((r) => r.sentence).join('\n\n'),
      };
      setLoading(false);
      persistLocal();
    }
  });
}
